/** The reusable timeline. Props in, pixels out — it never fetches.
 *
 *  That boundary is the whole point: the Overview page builds a `TimelineModel`
 *  from `/notification_timeline/`, and a future AI-proposal screen will build
 *  one from a structure that exists only in memory. Both render this.
 *
 *  Omit `actions` and it is read-only, which is exactly what a proposal wants —
 *  there is nothing to toggle on a notification that does not exist yet.
 */
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import { isLive } from './format';
import type {
  Density,
  TimelineActions,
  TimelineModel,
  TimelineNotification,
  TimelineStats,
} from './model/types';
import { NodeInspector } from './NodeInspector';
import { TestRenderForNotification } from './TestRenderForNotification';
import { TimelineRows } from './TimelineRows';

export interface NotificationTimelineProps {
  model: TimelineModel;
  density: Density;
  /** Optional overlay. Absent, or `available: false` ⇒ no numbers rendered. */
  stats?: TimelineStats;
  /** Absent ⇒ read-only. */
  actions?: TimelineActions;
  /** Ids currently being written, so their controls disable without a spinner. */
  busyNotificationIds?: ReadonlySet<number>;
  busyMethodIds?: ReadonlySet<number>;
  busyEvents?: ReadonlySet<string>;
  /** Rendered when the model has no rows at all. */
  emptyState?: React.ReactNode;
}

export function NotificationTimeline({
  model,
  density,
  stats,
  actions,
  busyNotificationIds,
  busyMethodIds,
  busyEvents,
  emptyState,
}: NotificationTimelineProps) {
  const [inspected, setInspected] = useState<TimelineNotification | null>(null);
  // The test-render dialog is its own overlay, independent of the drawer:
  // picking "Test render" should open the dialog and nothing else.
  const [testRenderId, setTestRenderId] = useState<number | null>(null);

  const openTestRender = useCallback((nf: TimelineNotification) => {
    if (nf.id !== null) setTestRenderId(nf.id);
  }, []);

  const timelineActions = useMemo(
    () => (actions ? { ...actions, onTestRender: openTestRender } : undefined),
    [actions, openTestRender],
  );

  // Keep the drawer in sync with the model: after a toggle the object identity
  // changes, and a stale copy would show the old switch state.
  const liveInspected = useMemo(() => {
    if (!inspected) return null;
    return model.notifications.find((n) => n.key === inspected.key) ?? inspected;
  }, [inspected, model.notifications]);

  if (!model.bands.length) {
    return (
      <>{emptyState ?? <Typography color="text.secondary">Nothing to show.</Typography>}</>
    );
  }

  const hiddenTotal = Object.values(model.hiddenNotificationCounts ?? {}).reduce(
    (sum, n) => sum + (n ?? 0),
    0,
  );

  return (
    <>
      {hiddenTotal > 0 && (
        // Hiding a band must never quietly swallow configured rows.
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {hiddenTotal} itinerary-beat notification{hiddenTotal === 1 ? '' : 's'} not shown —
          beats are anchored per tour, not to the guest journey.
        </Typography>
      )}
      <Stack spacing={2}>
        {model.bands.map((band) => (
          <Paper key={band.band} variant="outlined" sx={{ p: 1.5 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', mb: 1 }}
              id={`timeline-band-${band.band}`}
            >
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                }}
              >
                {band.label}
              </Typography>
              <Chip
                size="small"
                label={band.notificationCount}
                sx={{ height: 18, fontSize: '0.6875rem' }}
                variant="outlined"
              />
              {band.band === 'itinerary' && (
                <Typography variant="caption" color="text.secondary">
                  offsets are per-tour — set on each notification, not the event
                </Typography>
              )}
              {band.band === 'unknown' && (
                <Typography variant="caption" color="warning.main">
                  event codes the catalog does not recognise
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
            </Stack>

            {band.groups.map((group) => (
              <Box key={group.key} sx={{ mb: group.label ? 1.5 : 0 }}>
                {group.label && (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'baseline', mb: 0.5, pl: 0.5 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {group.label}
                    </Typography>
                    {group.hint && (
                      <Typography variant="caption" color="text.secondary">
                        {group.hint}
                      </Typography>
                    )}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={group.notificationCount}
                      sx={{ height: 18, fontSize: '0.6875rem' }}
                    />
                    <Box sx={{ flex: 1, borderTop: '1px solid', borderColor: 'divider' }} />
                  </Stack>
                )}
                <TimelineRows
                  rows={group.rows}
                  milestones={group.milestones}
                  density={density}
                  actions={timelineActions}
                  stats={stats}
                  onInspect={setInspected}
                  busyNotificationIds={busyNotificationIds}
                  busyMethodIds={busyMethodIds}
                  busyEvents={busyEvents}
                  showRail={band.band === 'lifecycle'}
                  tenantId={model.tenant?.id ?? null}
                />
              </Box>
            ))}
          </Paper>
        ))}
      </Stack>

      <NodeInspector
        notification={liveInspected}
        onClose={() => setInspected(null)}
        actions={timelineActions}
        stats={stats}
        onOpenTestRender={setTestRenderId}
      />

      {testRenderId !== null && (
        <TestRenderForNotification
          notificationId={testRenderId}
          onClose={() => setTestRenderId(null)}
        />
      )}
    </>
  );
}

/** Live/total counts for the toolbar. Exported so the page can show them
 *  above the component without reaching into the model itself. */
export function timelineCounts(model: TimelineModel): {
  notifications: number;
  live: number;
} {
  return {
    notifications: model.notifications.length,
    live: model.notifications.filter(isLive).length,
  };
}
